/**
 * DigitalSnow — canvas-based falling abstract symbols.
 * Matrix/Blade Runner aesthetic but with geometric glyphs instead of katakana.
 * Warm accent-tinted, with fading trails.
 */

import { useRef, useEffect, memo } from 'react';

const GLYPHS = '◇○△□·∘⋄▵▿◌◍⬡✦⊹⊕⊙';
const COLUMN_COUNT = 30;

interface Column {
  x: number;       // logical px
  y: number;       // logical px
  speed: number;   // logical px per frame
  char: string;
  opacity: number;
  size: number;    // font size in logical px
  trail: number;
}

export const DigitalSnow = memo(function DigitalSnow({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId = 0;
    let columns: Column[] = [];
    let logicalW = 0;
    let logicalH = 0;

    const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)];

    const makeColumn = (i: number, randomY: boolean): Column => ({
      x: (i / COLUMN_COUNT) * logicalW + Math.random() * (logicalW / COLUMN_COUNT),
      y: randomY ? Math.random() * logicalH : -10 - Math.random() * 60,
      speed: 0.2 + Math.random() * 0.6,
      char: randomGlyph(),
      opacity: 0.1 + Math.random() * 0.18,
      size: 9 + Math.random() * 5,
      trail: 2 + Math.floor(Math.random() * 3),
    });

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      logicalW = rect.width;
      logicalH = rect.height;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Re-init columns in logical coordinates
      columns = [];
      for (let i = 0; i < COLUMN_COUNT; i++) {
        columns.push(makeColumn(i, true));
      }
    };

    const draw = () => {
      if (logicalW === 0 || logicalH === 0) {
        animId = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, logicalW, logicalH);

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];

        // Draw lead character + fading trail above it
        for (let t = 0; t < col.trail; t++) {
          const ty = col.y - t * col.size * 1.4;
          const a = col.opacity * (1 - t * 0.3);
          if (ty < -20 || a <= 0) continue;
          ctx.font = `${col.size}px monospace`;
          ctx.fillStyle = `rgba(200, 85, 65, ${a})`;
          ctx.fillText(col.char, col.x, ty);
        }

        // Advance
        col.y += col.speed;

        // Occasionally swap glyph
        if (Math.random() < 0.01) col.char = randomGlyph();

        // Reset when off screen
        if (col.y > logicalH + 40) {
          columns[i] = makeColumn(i, false);
        }
      }

      animId = requestAnimationFrame(draw);
    };

    resize();
    animId = requestAnimationFrame(draw);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
});
